import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Flag, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * EventDetailDialog — shows full event details in a modal.
 * Pass `event` (full event doc) + `children` as the trigger element.
 * Optional `onRsvpChange` callback fires after successful RSVP.
 */
export default function EventDetailDialog({ event, children, onRsvpChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localEvent, setLocalEvent] = useState(event);

  const joined = user && (localEvent.rsvps || []).includes(user.user_id);

  const rsvp = async () => {
    if (!user) {
      toast.error("Please log in to RSVP");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${localEvent.event_id}/rsvp`);
      const rsvps = data.joined
        ? [...(localEvent.rsvps || []), user.user_id]
        : (localEvent.rsvps || []).filter((id) => id !== user.user_id);
      setLocalEvent({ ...localEvent, rsvps });
      toast.success(data.joined ? "RSVP confirmed!" : "RSVP cancelled.");
      onRsvpChange?.();
    } catch {
      toast.error("Could not RSVP");
    } finally {
      setBusy(false);
    }
  };

  const eventDate = new Date(localEvent.date);
  const dateStr = eventDate.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
  const timeStr = eventDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid={`event-dialog-${localEvent.event_id}`}
        className="bg-zinc-950 border-zinc-800 text-white rounded-none max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        {localEvent.image && (
          <div className="aspect-[16/8] bg-zinc-800 overflow-hidden border-b border-zinc-800">
            <img src={localEvent.image} alt={localEvent.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6 sm:p-8">
          <DialogHeader className="space-y-2">
            <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">
              OSCR EVENT
            </p>
            <DialogTitle className="font-display text-3xl sm:text-4xl text-white leading-[1.05] tracking-wide">
              {localEvent.title}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DetailRow icon={Calendar} label="DATE" value={dateStr} />
            <DetailRow icon={Clock} label="TIME" value={timeStr} />
            <DetailRow icon={MapPin} label="LOCATION" value={localEvent.location || "TBA"} />
            {localEvent.distance_km && (
              <DetailRow icon={Flag} label="DISTANCE" value={`${localEvent.distance_km} KM`} />
            )}
          </div>

          {localEvent.description && (
            <div className="mt-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">
                ABOUT THIS RUN
              </p>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                {localEvent.description}
              </p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap pt-5 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-zinc-400">
              <Users size={16} className="text-orange-500" />
              <span className="font-mono text-sm tracking-wider">
                <span className="text-white font-display text-lg">{(localEvent.rsvps || []).length}</span>{" "}
                <span className="text-[11px] tracking-[0.25em] uppercase">runners going</span>
              </span>
            </div>
            <Button
              onClick={rsvp}
              disabled={busy}
              data-testid={`dialog-rsvp-${localEvent.event_id}`}
              className={`rounded-none font-display tracking-wider px-6 ${
                joined
                  ? "bg-zinc-800 hover:bg-zinc-700 text-white"
                  : "bg-orange-600 hover:bg-orange-500 text-white"
              }`}
            >
              {busy ? "…" : joined ? "GOING ✓ · TAP TO CANCEL" : user ? "RSVP NOW" : "LOG IN TO RSVP"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="hairline bg-zinc-900 p-3 flex items-start gap-3">
      <Icon className="text-orange-500 shrink-0 mt-0.5" size={16} />
      <div className="min-w-0">
        <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">{label}</p>
        <p className="text-white text-sm mt-0.5 truncate">{value}</p>
      </div>
    </div>
  );
}
